import { createElement, forwardRef } from 'react';
import { Heading } from '../../Heading';
import { ObjectSwitcherItem, ObjectSwitcherPanelProps, ObjectSwitcherSection } from '../types';
import bundle from '../NovaComponents-ObjectManager-gui-icu.properties';
import { createUseTextLocalizer } from '../../../locale/createUseTextLocalizer';
import { useFocusNavigation, ItemLocation, MenuListDesign } from '../../Menu/__internal__';
import { useDesignTokens, renderInlineCssVars } from '../../../theme';
import { Button, ButtonVariant } from '../../Button';
import { Divider } from '../../Divider';
import { DIRTY_INDICATOR } from '../shared';
import { ObjectSwitcherMenuItem, ObjectSwitcherMenuSection } from '../__internal__/types';

import cn from './ObjectSwitcherPanel.css.json';
import './ObjectSwitcherPanel.css';

const useTextLocalizer = createUseTextLocalizer(bundle);

export const headingId = 'object-switcher-panel-header';

/**
 * The panel used by the ObjectSwitcher
 * @see https://rndconfluence.sas.com/confluence/pages/viewpage.action?spaceKey=NOVA&title=ObjectManager
 */
export const ObjectSwitcherPanel = forwardRef<HTMLDivElement, ObjectSwitcherPanelProps>(
  function ObjectSwitcherPanel(props, forwardRef) {
    const localize = useTextLocalizer();
    const sections = switcherSectionsToMenuSections(props.sections);

    const { focusLocation, onKeyDown } = useFocusNavigation({
      sections,
      isOpen: true,
      defaultFocusLocation: sections.length > 0 ? [0, 0] : undefined,
    });

    const dt = useDesignTokens();
    const style = renderInlineCssVars(
      {
        'spacing-l': dt.spacing.core.l,
        'spacing-m': dt.spacing.core.m,
        'spacing-xl': dt.spacing.core.xl,
        'font-weight-thick': dt.fontNova.weight.thick,
      },
      'object-switcher-panel'
    );

    return (
      <div ref={forwardRef} className={cn.container} style={style}>
        <Heading id={headingId} level={3} className={cn.heading}>
          {localize('objectManager.objectSwitcher.panel.openedItems.title')}
        </Heading>
        <MenuListDesign
          firstItemRef={props.firstItemRef}
          tabIndex={-1}
          className={cn.list}
          onKeyDown={onKeyDown}
          sections={sections}
          focusLocation={focusLocation}
          disabled={false}
          onItemSelected={(itemLocation) => {
            props.onObjectChange?.({
              item: lookupViaItemLocation(props.sections, itemLocation)!,
            });
          }}
          onItemClosed={(itemLocation) => {
            props.onObjectClose?.({
              item: lookupViaItemLocation(props.sections, itemLocation)!,
            });
          }}
          showFocus={props.showFocus ?? true}
        />
        <Divider className={cn.divider} />
        <div className={cn.buttonContainer}>
          <Button
            text={localize('objectManager.objectSwitcher.panel.closeAllItems.button.label')}
            variant={ButtonVariant.Light}
            onPress={props.onObjectCloseAll}
          />
        </div>
      </div>
    );
  }
);

const switcherSectionsToMenuSections = (switcherSections: readonly ObjectSwitcherSection[]) => {
  const menuSections: ObjectSwitcherMenuSection[] = [];

  const formatTitle = (text: string, isDirty?: boolean) =>
    isDirty ? `${DIRTY_INDICATOR}${text}` : text;

  const flattenSubItems = (items: readonly ObjectSwitcherItem[]) => {
    const _flattenSubItems = (items: readonly ObjectSwitcherItem[], level: number) => {
      let menuItems: ObjectSwitcherMenuItem[] = [];
      for (const item of items) {
        menuItems.push({
          ...item,
          text: formatTitle(item.text, item.isDirty),
          indentation: level,
        });
        if (Array.isArray(item.items)) {
          menuItems = menuItems.concat(_flattenSubItems(item.items, level + 1));
        }
      }
      return menuItems;
    };
    return _flattenSubItems(items, 1);
  };

  for (const { items, title } of switcherSections) {
    let menuItems: ObjectSwitcherMenuItem[] = [];
    for (const item of items) {
      menuItems.push({
        ...item,
        text: formatTitle(item.text, item.isDirty),
      });
      if (items) {
        menuItems = menuItems.concat(flattenSubItems(item.items ?? []));
      }
    }

    menuSections.push({
      title,
      items: menuItems.map((menuItem) => ({
        closable: true,
        ...menuItem,
      })),
    });
  }
  return menuSections;
};

const lookupViaItemLocation = (
  switcherSections: readonly ObjectSwitcherSection[],
  itemLocation: ItemLocation[]
) => {
  const lookupSectionViaKey = (key: string, switcherSections: readonly ObjectSwitcherSection[]) => {
    const lookupItem = (items: readonly ObjectSwitcherItem[]): ObjectSwitcherItem | null => {
      for (const currentItem of items) {
        if (currentItem.key === key) return currentItem;
        if (currentItem.items) {
          const foundItem = lookupItem(currentItem.items);
          if (foundItem) return foundItem;
        }
      }
      return null;
    };
    for (const section of switcherSections) {
      const foundItem = lookupItem(section.items);
      if (foundItem) return foundItem;
    }
  };

  const sections = switcherSectionsToMenuSections(switcherSections);
  const [sectionIndex, itemIndex] = itemLocation[0];
  const targetItem = sections[sectionIndex].items[itemIndex];
  return lookupSectionViaKey(targetItem.key, switcherSections);
};
